import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  CalendarIcon,
  CalendarCheck2Icon,
  EyeIcon,
  SearchIcon,
  CheckIcon,
  XIcon,
  SendHorizonalIcon,
  CopyIcon
} from 'lucide-react';
import { type AppointmentMode, tenantApi, type Appointment, type AppointmentStatus } from '../lib/tenant-api';
import { PageLoader, Pagination } from '../components/common';
import clsx from 'clsx';
import { StatCard } from '../components/StatCard';

interface AppointmentsPageProps { }

const Appointments: React.FC<AppointmentsPageProps> = () => {
  // State for pagination and filtering
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<AppointmentStatus | ''>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [copiedText, setCopiedText] = useState<string | null>(null);

  const limit = 20;
  const showClearFilters = statusFilter !== '' || searchTerm !== '';

  // Copy to clipboard function
  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedText(text);
      // Clear the copied state after 2 seconds
      setTimeout(() => setCopiedText(null), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        setCopiedText(text);
        setTimeout(() => setCopiedText(null), 2000);
      } catch (fallbackErr) {
        console.error('Fallback copy failed: ', fallbackErr);
      }
      document.body.removeChild(textArea);
    }
  };

  // Copy button component
  const CopyButton: React.FC<{ text: string; label?: string, showIcon?: boolean }> = ({ text, label, showIcon = false }) => {
    const isCopied = copiedText === text;

    return (
      <button
        onClick={() => copyToClipboard(text)}
        className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 transition-colors"
        title={`Copy ${label || 'text'}`}
      >
        {isCopied ? (
          <>
            <CheckIcon className="h-3 w-3 text-green-600" />
            <span className="text-green-600">Copied!</span>
          </>
        ) : (
          <>
            {showIcon && <CopyIcon className="h-3 w-3" />}
            <span className="text-sm font-medium text-indigo-600 hover:text-indigo-800">{label || 'Copy'}</span>
          </>
        )}
      </button>
    );
  };

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      // Only update debounced search if it's empty, or has more than 3 characters
      if (searchTerm.length === 0 || searchTerm.length > 3) {
        setDebouncedSearchTerm(searchTerm);
        setCurrentPage(1); // Reset to first page when search changes
      }
    }, 2000); // 2 second delay

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset page when status filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter]);

  // Fetch appointments
  const { data: appointmentsData, isLoading, error } = useQuery({
    queryKey: ['appointments', currentPage, statusFilter, debouncedSearchTerm],
    queryFn: () => tenantApi.getAppointments({
      page: currentPage,
      limit: limit,
      status: statusFilter || undefined,
      query: debouncedSearchTerm || undefined
    }),
  });

  // Fetch appointment stats
  const { data: stats } = useQuery({
    queryKey: ['appointment-stats'],
    queryFn: tenantApi.getAppointmentStats,
  });

  const appointments = appointmentsData?.appointments || [];

  const handleClearFilters = () => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setStatusFilter('');
    setCurrentPage(1);
  };

  const handleViewAppointment = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getModeBadge = (mode: AppointmentMode) => {
    const statusClasses = {
      request_only: {
        className: 'bg-green-100 text-grey-300',
        text: 'Request',
        icon: <SendHorizonalIcon className="h-3 w-3 mr-3 text-gray-400" />
      },
      auto_schedule: {
        className: 'bg-purple-100 text-grey-300',
        text: 'Scheduled',
        icon: <CalendarCheck2Icon className="h-3 w-3 mr-3 text-gray-400" />
      },
      both_methods: {
        className: 'bg-indigo-100 text-grey-300',
        text: 'Flexible',
        icon: <CalendarIcon className="h-3 w-3 mr-3 text-gray-400" />
      },
    };

    return (
      <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${statusClasses[mode]?.className || 'bg-gray-100 text-gray-300'}`}>
        {statusClasses[mode]?.icon}
        {statusClasses[mode]?.text || (mode.charAt(0).toUpperCase() + mode.slice(1).replace('_', ' '))}
      </span>
    );
  };

  // Get the primary appointment date (first selected time)
  const getPrimaryAppointmentDates = (available_times: string[]) => {
    return available_times.length > 0 ?
      available_times.map(time => `${formatDateTime(time)}`) : [];
  };

  // Check if search is in progress (user is typing but hasn't finished)
  const isSearchInProgress = searchTerm !== debouncedSearchTerm && searchTerm.length > 0 && searchTerm.length <= 3;

  if (isLoading) {
    return <PageLoader />;
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-red-600">Error loading appointments</div>
      </div>
    );
  }

  return (
    <div>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold leading-7 text-gray-900 sm:truncate sm:text-3xl sm:tracking-tight">
            Appointments
          </h1>
          <p className="mt-2 text-sm text-gray-700">
            View and manage appointment requests and scheduled appointments.
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
          <StatCard
            icon={CalendarIcon}
            title="Total Appointments"
            value={stats?.total_appointments ?? 0}
          />

          <StatCard
            icon={SendHorizonalIcon}
            title="Appointment Requests"
            value={stats?.pending_requests ?? 0}
          />

          <StatCard
            icon={CalendarCheck2Icon}
            title="Scheduled Appointments"
            value={stats?.scheduled_appointments ?? 0}
          />
        </div>

        {/* Filters */}
        <div className="bg-white shadow  p-6">
          {/* <div className="grid grid-cols-1 md:grid-cols-6 gap-4 w-full"> */}
          <div className="flex flex-col sm:flex-row flex-wrap gap-4 w-full">
            <div className='md:col-span-4 grow'>
              <div className="relative">
                <SearchIcon className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  id="search"
                  placeholder="Search by client name or email address"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-8 pr-3 py-2 border border-gray-300  shadow-sm focus:outline-none focus:ring-1 focus:ring-gray-400 focus:border-gray-500"

                />
                {isSearchInProgress && (
                  <div className="absolute right-3 top-3">
                    <div className="animate-spin h-4 w-4 border-2 border-yellow-500 border-t-transparent rounded-full"></div>
                  </div>
                )}
              </div>
            </div>

            <div>
              <select
                id="status-filter"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as AppointmentStatus | '')}
                className="py-2 min-h-[42px] block w-full  border border-gray-300 shadow-sm focus:outline-none focus:border-gray-400 sm:text-sm px-3"
              >
                <option value="">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="confirmed">Confirmed</option>
                <option value="cancelled">Cancelled</option>
                <option value="completed">Completed</option>
                <option value="no_show">No Show</option>
              </select>
            </div>

            <div className="flex items-end">
              <button
                onClick={handleClearFilters}
                className={
                  clsx("px-4 py-2 border border-gray-300  shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500",
                    !showClearFilters ? 'opacity-50 cursor-not-allowed' : ''
                  )
                }
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>

        {/* Appointments List */}
        <div className="bg-white shadow ">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              Recent Appointments ({appointmentsData?.total || 0})
            </h3>

            {appointments.length === 0 ? (
              <div className="text-center py-8">
                <CalendarIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No appointments found</h3>
                <p className="mt-1 text-sm text-gray-500">
                  {searchTerm || statusFilter ? 'Try adjusting your filters.' : 'Appointments will appear here when users book through your AI assistant.'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full min-w-[600px] divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        Client
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        Dates & Times
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        Created
                      </th>
                      <th className="relative px-6 py-3">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {appointments.map((appointment) => (
                      <tr key={appointment.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {appointment.full_name}
                              </div>
                              <div className="text-sm font-medium text-gray-500">
                                {appointment.email}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {getPrimaryAppointmentDates(appointment.available_times).map((date, index) => (
                            <div className='mb-1' key={index}>{date}</div>
                          ))}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {/* TODO: Update when we start offereing full integration */}
                          {getModeBadge('request_only')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(appointment.created_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleViewAppointment(appointment)}
                              className="flex items-center px-3 py-1 text-sm gap-x-2"
                              title="View appointment details"
                            >
                              <EyeIcon className="h-4 w-4" />
                              View details
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {appointmentsData && appointmentsData.total > limit && (
              <Pagination
                className="mt-6"
                page={currentPage}
                totalPages={Math.max(1, Math.ceil(appointmentsData.total / limit))}
                onPageChange={setCurrentPage}
                rangeStart={(currentPage - 1) * limit + 1}
                rangeEnd={Math.min(currentPage * limit, appointmentsData.total)}
                totalItems={appointmentsData.total}
                hasPrevPage={appointmentsData.has_prev}
                hasNextPage={appointmentsData.has_next}
              />
            )}
          </div>
        </div>

      </div>

      {/* Appointment Detail Modal */}
      {selectedAppointment && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 px-2 sm:px-4">
          <div className="relative top-10 sm:top-20 mx-auto p-0 border w-full max-w-lg shadow-lg bg-white max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                Appointment Request Details
              </h3>
              <button
                onClick={() => setSelectedAppointment(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <span className="sr-only">Close</span>
                <XIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6">
              {/* Client Information Section */}
              <div className="mb-8">
                <h4 className="text-sm font-medium text-gray-900 mb-4">Client information</h4>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Name</span>
                    <span className="text-sm font-medium text-gray-900">{selectedAppointment.full_name}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Email address</span>
                    <span className="text-sm font-medium text-gray-900">{selectedAppointment.email}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Phone number</span>
                    <span className="text-sm font-medium text-gray-900">{selectedAppointment.phone || 'Not provided'}</span>
                  </div>
                </div>
              </div>

              {/* Appointment Information Section */}
              <div className="mb-8">
                <h4 className="text-sm font-medium text-gray-900 mb-4">Appointment information</h4>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Type</span>
                    <span className="text-sm font-medium text-gray-900">Appointment request</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Date</span>
                    <span className="text-sm font-medium text-gray-900">
                      {selectedAppointment.available_times.length > 0 ?
                        formatDate(selectedAppointment.available_times[0]) :
                        'Not specified'
                      }
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Time</span>
                    <span className="text-sm font-medium text-gray-900">
                      {selectedAppointment.available_times.length > 0 ?
                        selectedAppointment.available_times.map(time => {
                          const timeOnly = new Date(time).toLocaleTimeString('en-US', {
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: true
                          });
                          return timeOnly;
                        }).join(', ') :
                        'Not specified'
                      }
                    </span>
                  </div>
                  {selectedAppointment.services && selectedAppointment.services.length > 0 && (
                    <div className="flex justify-between items-start">
                      <span className="text-sm text-gray-600">Services</span>
                      <div className="text-right">
                        <span className="text-sm font-medium text-gray-900">
                          {selectedAppointment.services.join(', ')}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Request Information Section */}
              <div className="mb-8 bg-green-50 p-4 ">
                <h4 className="text-sm font-medium text-gray-900 mb-4">Request information</h4>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Request sent to</span>
                    <span className="text-sm font-medium text-gray-900">{selectedAppointment.sent_to ?? "--"}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Sent at</span>
                    <span className="text-sm font-medium text-gray-900">
                      {selectedAppointment.sent_at ? formatDateTime(selectedAppointment.sent_at) : "--"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Additional Information */}
              <div className="space-y-3 pt-4 border-t border-gray-200">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Created</span>
                  <span className="text-sm font-medium text-gray-900">
                    {formatDateTime(selectedAppointment.created_at)}
                  </span>
                </div>
                {selectedAppointment.conversation_id && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Conversation ID</span>
                    <CopyButton text={selectedAppointment.conversation_id} label="Copy ID" />
                  </div>
                )}
              </div>

              {/* Notes Section */}
              {selectedAppointment.notes && (
                <div className="mt-6 p-4 bg-gray-50 ">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Notes</h4>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">
                    {selectedAppointment.notes}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Appointments;
